import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import QRCode from "qrcode";
import JsBarcode from "jsbarcode";
import { api, type Inspection, type Session } from "./api";

const VERSION = "1.0.0";
const SAVED_EMAIL = "mv.savedEmail";

const COPY: Record<string, { title: string; desc: string; short: string }> = {
  ADMITTED: { title: "정상 입장", desc: "입장이 가능합니다.", short: "정상" },
  PARTIAL_ADMITTED: { title: "부분 입장", desc: "요청한 인원만 입장 처리했습니다.", short: "부분입장" },
  ALREADY_USED: { title: "이미 사용된 티켓", desc: "해당 티켓은 이미 입장 처리되었습니다.", short: "이미사용" },
  CANCELLED: { title: "취소된 티켓", desc: "취소된 예약은 입장할 수 없습니다.", short: "취소" },
  INVALID_PERIOD: { title: "이용 기간/시간 오류", desc: "이용 기간 또는 시간이 올바르지 않습니다.", short: "시간오류" },
  UNKNOWN_TICKET: { title: "미등록 티켓", desc: "등록되지 않은 티켓입니다.", short: "미등록" },
  INSUFFICIENT_REMAINING: { title: "잔여 인원 부족", desc: "남은 인원보다 많은 입장을 요청했습니다.", short: "인원부족" },
  UNCONFIRMED: { title: "결과 미확인", desc: "발권사 응답을 확인하지 못했습니다. 새 코드로 다시 찍지 말고 같은 요청을 조회하십시오.", short: "미확인" },
  ISSUER_UNAVAILABLE: { title: "네트워크 연결 오류", desc: "발권사에 연결하지 못했습니다. 네트워크를 확인한 뒤 재시도하십시오.", short: "통신장애" },
  OFFLINE_DENIED: { title: "네트워크 연결 오류", desc: "오프라인 자동 승인은 하지 않습니다. 연결을 확인한 뒤 다시 시도하십시오.", short: "통신장애" },
  DUPLICATE_NOT_GUARANTEED: { title: "중복 차단 불가", desc: "이 발권사는 사용 처리를 원자적으로 하지 않아 입장을 확정하지 않습니다.", short: "중복불가" },
  LICENSE_INACTIVE: { title: "사용 기간 아님", desc: "이 고객사의 사용 기간이 아닙니다.", short: "기간오류" },
  REJECTED: { title: "입장 불가", desc: "입장할 수 없는 티켓입니다.", short: "거부" },
  pending: { title: "처리 중", desc: "결과를 확인하는 중입니다.", short: "처리중" },
};

type Tab = "scan" | "history" | "ops" | "admin" | "samples";
type Tone = "pass" | "fail" | "warn" | "net";

function toneOf(decision: string): Tone {
  if (decision === "ADMITTED" || decision === "PARTIAL_ADMITTED") return "pass";
  if (decision === "INVALID_PERIOD" || decision === "LICENSE_INACTIVE") return "warn";
  if (decision === "UNCONFIRMED" || decision === "ISSUER_UNAVAILABLE" || decision === "OFFLINE_DENIED") return "net";
  return "fail";
}

function histClass(decision: string) {
  const tone = toneOf(decision);
  if (tone === "pass") return "va-hist__ok";
  if (tone === "warn" || tone === "net") return "va-hist__warn";
  return "va-hist__fail";
}

function readSavedEmail() {
  try {
    return localStorage.getItem(SAVED_EMAIL) ?? "";
  } catch {
    return "";
  }
}

function writeSavedEmail(email: string | null) {
  try {
    if (email) localStorage.setItem(SAVED_EMAIL, email);
    else localStorage.removeItem(SAVED_EMAIL);
  } catch {
    /* 저장 공간이 없어도 로그인은 계속한다. */
  }
}

function seoulDay(value: string | null) {
  if (!value) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(value));
}

function seoulClock(value: string | null) {
  if (!value) return "";
  return new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Seoul", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" }).format(new Date(value));
}

function seoulStamp(value: string | null) {
  const day = seoulDay(value);
  if (!day) return "";
  return `${day.replaceAll("-", ".")} ${seoulClock(value)}`;
}

function todaySeoul() {
  return seoulDay(new Date().toISOString());
}

function cameraGuidance(error: Error) {
  if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
    return "카메라 권한이 거부되었습니다. 브라우저 주소창의 사이트 설정에서 카메라를 허용한 뒤 다시 누르십시오.";
  }
  if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
    return "카메라를 찾지 못했습니다. 입력칸에 티켓 코드를 직접 입력하십시오.";
  }
  if (error.name === "NotReadableError" || error.name === "TrackStartError") {
    return "다른 앱이 카메라를 사용 중입니다. 그 앱을 닫고 다시 시도하십시오.";
  }
  return "카메라를 열 수 없습니다. 코드를 직접 입력하거나, 권한을 확인한 뒤 다시 시도하십시오.";
}

function PersonIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-4 0-8 2-8 4v2h16v-2c0-2-4-4-8-4Z" /></svg>;
}

function LockIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M17 9h-1V7a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2Zm-7-2a2 2 0 0 1 4 0v2h-4Zm7 12H7v-8h10Z" /></svg>;
}

function EyeIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5C7 5 2.7 8.1 1 12c1.7 3.9 6 7 11 7s9.3-3.1 11-7c-1.7-3.9-6-7-11-7Zm0 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0-8a3 3 0 1 1-3 3 3 3 0 0 1 3-3Z" /></svg>;
}

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [tab, setTab] = useState<Tab>("scan");
  const [menu, setMenu] = useState(false);
  const [link, setLink] = useState({ bad: false, label: "확인 중" });

  useEffect(() => {
    api.me().then(setSession).catch(() => setSession(null));
  }, []);

  useEffect(() => {
    if (!session) return;
    const load = () => {
      api.status().then((status) => {
        const down = status.issuers.filter((item) => !item.ok);
        setLink(down.length
          ? { bad: true, label: "연결 끊김" }
          : { bad: false, label: "정상" });
      }).catch(() => setLink({ bad: true, label: "연결 끊김" }));
    };
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [session]);

  if (!session) return <Login onSuccess={setSession} />;

  const canOps = session.role === "supervisor" || session.role === "tenant_admin" || session.role === "platform_admin";
  const canAdmin = session.role === "tenant_admin" || session.role === "platform_admin";
  const title = tab === "history" ? "이력 조회" : tab === "samples" ? "샘플 코드" : tab === "ops" ? "게이트" : tab === "admin" ? "관리" : "검표";

  function open(next: Tab) {
    setTab(next);
    setMenu(false);
  }

  return (
    <div className="shell">
      <header className="validator-app__header">
        <div className="validator-app__header-left">
          {tab === "scan" ? (
            <button className="validator-app__burger" type="button" aria-label="메뉴" aria-expanded={menu} onClick={() => setMenu((value) => !value)}>
              <span /><span /><span />
            </button>
          ) : (
            <button className="validator-app__back" type="button" aria-label="검표로 돌아가기" onClick={() => open("scan")}><i /></button>
          )}
        </div>
        <div className="validator-app__header-title">{title}</div>
        <button className="validator-app__header-right" type="button" onClick={() => api.logout().finally(() => setSession(null))}>로그아웃</button>
      </header>
      <div className="shell__main">
        {menu && (
          <nav className="va-menu" aria-label="화면 이동">
            <button type="button" aria-current={tab === "scan" ? "page" : undefined} onClick={() => open("scan")}>검표</button>
            <button type="button" aria-current={tab === "history" ? "page" : undefined} onClick={() => open("history")}>이력 조회</button>
            <button type="button" aria-current={tab === "samples" ? "page" : undefined} onClick={() => open("samples")}>샘플 코드</button>
            {canOps && <button type="button" aria-current={tab === "ops" ? "page" : undefined} onClick={() => open("ops")}>게이트</button>}
            {canAdmin && <button type="button" aria-current={tab === "admin" ? "page" : undefined} onClick={() => open("admin")}>관리</button>}
          </nav>
        )}
        {tab === "scan" && <ScanScreen staffName={session.displayName} linkBad={link.bad} />}
        {tab === "history" && <HistoryScreen />}
        {tab === "samples" && <SampleScreen />}
        {tab === "ops" && canOps && <OpsScreen />}
        {tab === "admin" && canAdmin && <AdminScreen session={session} />}
      </div>
      <footer className="validator-app__status">
        <span className="validator-app__status-left">
          <span className={link.bad ? "validator-app__dot is-off" : "validator-app__dot"} />
          연결 상태: <span className={link.bad ? "validator-app__bad" : "validator-app__ok"}>{link.label}</span>
        </span>
        <span>VER {VERSION}</span>
      </footer>
    </div>
  );
}

function Login({ onSuccess }: { onSuccess: (session: Session) => void }) {
  const saved = readSavedEmail();
  const [email, setEmail] = useState(saved);
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(Boolean(saved));
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  return (
    <form
      className="shell va-login"
      onSubmit={async (event) => {
        event.preventDefault();
        setError("");
        try {
          const session = await api.login(email, password);
          writeSavedEmail(remember ? email : null);
          onSuccess(session);
        } catch (err) {
          setError((err as Error).message);
        }
      }}
    >
      <div className="va-login__brand">
        <img className="va-login__logo" src="/brand/ticketbnk-logo-icon.png" alt="" width={36} height={36} />
        <div className="va-login__name"><em>티켓뱅크</em> SoT SYSTEMS</div>
      </div>
      <p className="va-login__sub">입장 검표 시스템</p>
      <label className="va-login__field">
        <PersonIcon />
        <input value={email} onChange={(event) => setEmail(event.target.value)} placeholder="이메일을 입력하세요" autoComplete="username" inputMode="email" onFocus={(event) => event.currentTarget.scrollIntoView({ block: "center" })} />
      </label>
      <label className="va-login__field">
        <LockIcon />
        <input type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="비밀번호를 입력하세요" autoComplete="current-password" onFocus={(event) => event.currentTarget.scrollIntoView({ block: "center" })} />
        <button className="va-login__eye" type="button" aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 표시"} onClick={() => setShowPassword((value) => !value)}>
          <EyeIcon />
        </button>
      </label>
      <label className="va-login__check">
        <input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} />
        이메일 저장
      </label>
      {error && <p className="va-error">{error}</p>}
      <button className="va-login__btn" type="submit">로그인</button>
      <p className="va-login__copy">© SoT SYSTEMS. All rights reserved.</p>
    </form>
  );
}

function ScanScreen({ staffName, linkBad }: { staffName: string; linkBad: boolean }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const manualRef = useRef<HTMLInputElement>(null);
  const [entrances, setEntrances] = useState<Array<{ id: string; name: string; vendor_code: string }>>([]);
  const [entranceId, setEntranceId] = useState("");
  const [adapters, setAdapters] = useState<Array<{ vendorCode: string; limitations: string[]; atomicConsume: boolean; supportsPartial: boolean }>>([]);
  const [mode, setMode] = useState<"partial" | "all">("partial");
  const [count, setCount] = useState(1);
  const [manual, setManual] = useState("");
  const [result, setResult] = useState<Inspection | null>(null);
  const [lastCode, setLastCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [stats, setStats] = useState({ total: 0, pass: 0, deny: 0, error: 0 });
  const seen = useRef("");

  function loadStats() {
    api.history().then((body) => {
      const today = todaySeoul();
      const items = body.items.filter((item) => seoulDay(item.createdAt) === today);
      const pass = items.filter((item) => toneOf(item.decision) === "pass").reduce((sum, item) => sum + item.admittedCount, 0);
      const deny = items.filter((item) => toneOf(item.decision) === "fail").length;
      const error = items.filter((item) => toneOf(item.decision) === "warn" || toneOf(item.decision) === "net").length;
      setStats({ total: items.length, pass, deny, error });
    }).catch(() => undefined);
  }

  useEffect(() => {
    api.context().then((context) => {
      setEntrances(context.entrances);
      setAdapters(context.adapters);
      if (context.entrances[0]) setEntranceId(context.entrances[0].id);
    }).catch((error: Error) => setCameraError(error.message));
    loadStats();
  }, []);

  useEffect(() => {
    if (!scanning || !videoRef.current) return;
    const reader = new BrowserMultiFormatReader();
    let stopped = false;
    let controls: { stop: () => void } | undefined;
    reader.decodeFromVideoDevice(undefined, videoRef.current, (found) => {
      const value = found?.getText();
      if (!value || stopped || value === seen.current) return;
      seen.current = value;
      submit(value);
    }).then((next) => {
      controls = next;
      if (stopped) next.stop();
    }).catch((error: Error) => {
      setScanning(false);
      setCameraError(cameraGuidance(error));
    });
    return () => {
      stopped = true;
      controls?.stop();
    };
  }, [scanning, entranceId, count, mode]);

  const entrance = entrances.find((item) => item.id === entranceId);
  const adapter = adapters.find((item) => item.vendorCode === entrance?.vendor_code);

  async function submit(code: string) {
    if (!entranceId || !code.trim()) return;
    setLastCode(code.trim());
    setCameraError("");
    try {
      const inspection = await api.inspect({
        requestId: crypto.randomUUID(),
        entranceId,
        code: code.trim(),
        admitCount: count,
        admitMode: mode,
      });
      setResult(inspection);
      setScanning(false);
      loadStats();
    } catch (error) {
      setCameraError((error as Error).message || "서버에 연결하지 못했습니다. 네트워크를 확인한 뒤 다시 시도하십시오.");
    }
  }

  if (result && toneOf(result.decision) === "net") {
    return (
      <section className="va-net">
        <div className="va-net__panel">
          <div className="va-net__wifi" aria-hidden="true" />
          <strong>{COPY[result.decision]?.title ?? "네트워크 연결 오류"}</strong>
          <p>{result.detail?.operatorAction || result.detail?.message || COPY[result.decision]?.desc}</p>
        </div>
        <button className="va-net__btn is-primary" type="button" onClick={() => {
          if (result.decision === "UNCONFIRMED" && lastCode) api.reconcile(result.id, lastCode).then(setResult);
          else submit(lastCode);
        }}>재시도</button>
        <button className="va-net__btn is-outline" type="button" onClick={() => setResult(null)}>다시 입력</button>
        <p className="va-net__note">오프라인 자동 승인은 하지 않습니다. 연결을 확인한 뒤 재시도하십시오.</p>
      </section>
    );
  }

  if (result) {
    return <ResultView result={result} entranceName={entrance?.name ?? ""} staffName={staffName} onClose={() => setResult(null)} />;
  }

  return (
    <section className="va-scan">
      <div className="va-scan__row">
        <div className="va-scan__field">
          <label htmlFor="entrance">입구</label>
          <select id="entrance" className="va-scan__select" value={entranceId} onChange={(event) => setEntranceId(event.target.value)}>
            {entrances.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </div>
        <div className="va-scan__field">
          <label>직원</label>
          <div className="va-scan__static">{staffName}</div>
        </div>
      </div>
      {adapter?.supportsPartial && (
        <div className="va-scan__row">
          <div className="va-scan__field">
            <label htmlFor="admit-mode">입장 방식</label>
            <select id="admit-mode" className="va-scan__select" value={mode} onChange={(event) => setMode(event.target.value as "partial" | "all")}>
              <option value="partial">부분 입장</option>
              <option value="all">일괄 입장</option>
            </select>
          </div>
          <div className="va-scan__field">
            <label htmlFor="admit-count">인원</label>
            <input id="admit-count" className="va-field" type="number" min={1} max={100} value={count} onChange={(event) => setCount(Number(event.target.value))} />
          </div>
        </div>
      )}
      {adapter && !adapter.atomicConsume && <p className="va-note">{adapter.limitations.join(" ")}</p>}
      {linkBad && <p className="va-note">발권 연결이 끊겼습니다. 네트워크를 확인한 뒤 다시 스캔하십시오. 오프라인 자동 승인은 하지 않습니다.</p>}
      <p className="va-scan__prompt">QR코드를 스캔해주세요</p>
      <div className={scanning ? "va-scan__box is-live" : "va-scan__box"}>
        {scanning ? <video ref={videoRef} muted playsInline /> : (
          <input ref={manualRef} className="va-scan__manual" value={manual} onChange={(event) => setManual(event.target.value)} placeholder="티켓 코드" enterKeyHint="done" onKeyDown={(event) => { if (event.key === "Enter") submit(manual); }} onFocus={(event) => event.currentTarget.scrollIntoView({ block: "center" })} />
        )}
        {!scanning && <button className="va-scan__cam" type="button" onClick={() => { setCameraError(""); setScanning(true); }}>카메라 열기</button>}
        <button className="va-scan__kbd" type="button" aria-label="코드 직접 입력" onClick={() => { setScanning(false); manualRef.current?.focus(); }} />
      </div>
      {cameraError && <p className="va-error">{cameraError}</p>}
      {!scanning && <button className="va-btn" type="button" onClick={() => submit(manual)}>확인</button>}
      {scanning && <button className="va-net__btn is-outline" type="button" onClick={() => setScanning(false)}>카메라 중지</button>}
      <div className="va-scan__stats-head">
        <span>검표 통계 (오늘)</span>
        <button className="va-scan__refresh" type="button" aria-label="통계 새로고침" onClick={loadStats} />
      </div>
      <div className="va-scan__grid">
        <div className="va-scan__card is-blue"><span>전체 검표</span><strong>{stats.total.toLocaleString("ko-KR")}건</strong></div>
        <div className="va-scan__card is-green"><span>정상 입장</span><strong>{stats.pass.toLocaleString("ko-KR")}명</strong></div>
        <div className="va-scan__card is-red"><span>거부/사용완료</span><strong>{stats.deny.toLocaleString("ko-KR")}건</strong></div>
        <div className="va-scan__card is-orange"><span>오류</span><strong>{stats.error.toLocaleString("ko-KR")}건</strong></div>
      </div>
    </section>
  );
}

function ResultView({ result, entranceName, staffName, onClose }: { result: Inspection; entranceName: string; staffName: string; onClose: () => void }) {
  const tone = toneOf(result.decision);
  const copy = COPY[result.decision] ?? { title: result.decision, desc: "", short: result.decision };
  const [left, setLeft] = useState(3);
  useEffect(() => {
    const timer = window.setInterval(() => setLeft((value) => value - 1), 1000);
    return () => window.clearInterval(timer);
  }, [result.id]);
  useEffect(() => {
    if (left <= 0) onClose();
  }, [left, onClose]);

  const rows: Array<[string, string]> = [];
  if (result.codeMask) rows.push(["표시 코드", result.codeMask]);
  if (result.vendorTicketRef) rows.push(["발권 참조", result.vendorTicketRef]);
  rows.push(["입장 인원", `${result.admittedCount}명`]);
  if (result.capacity != null) rows.push(["정원", `${result.capacity}명`]);
  if (result.remainingCount != null) rows.push(["잔여", `${result.remainingCount}명`]);
  if (result.decision === "ALREADY_USED" || result.decision === "ADMITTED" || result.decision === "PARTIAL_ADMITTED") {
    if (entranceName) rows.push(["사용 입구", entranceName]);
    if (staffName) rows.push(["사용 직원", staffName]);
  }
  if (result.createdAt) rows.push(["확인 시각", seoulStamp(result.createdAt)]);

  return (
    <section className="va-result">
      <div className={`va-result__panel is-${tone === "warn" ? "warn" : tone === "pass" ? "pass" : "fail"}`}>
        <div className="va-result__icon" aria-hidden="true">{tone === "pass" ? "✓" : tone === "warn" ? "!" : "✕"}</div>
        <p className="va-result__title">{copy.title}</p>
        <p className="va-result__desc">{copy.desc}</p>
      </div>
      <div className="va-info">
        <h4>예약 정보</h4>
        {rows.map(([label, value]) => (
          <div className="va-info__row" key={label}><dt>{label}</dt><dd>{value}</dd></div>
        ))}
        {result.detail?.message && <p className="va-info__message">{result.detail.message}</p>}
      </div>
      <button className="va-confirm" type="button" onClick={onClose}>확인 ({Math.max(left, 0)})</button>
    </section>
  );
}

function HistoryScreen() {
  const [items, setItems] = useState<Inspection[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [day, setDay] = useState(todaySeoul());
  const [entrance, setEntrance] = useState("전체");
  const [page, setPage] = useState(1);
  useEffect(() => {
    api.history().then((body) => setItems(body.items)).catch(() => setItems([])).finally(() => setLoaded(true));
  }, []);
  const entrances = ["전체", ...Array.from(new Set(items.map((item) => item.entranceName).filter(Boolean) as string[]))];
  const filtered = items.filter((item) => seoulDay(item.createdAt) === day && (entrance === "전체" || item.entranceName === entrance));
  const pass = filtered.filter((item) => toneOf(item.decision) === "pass").length;
  const deny = filtered.filter((item) => toneOf(item.decision) === "fail").length;
  const error = filtered.filter((item) => toneOf(item.decision) === "warn" || toneOf(item.decision) === "net").length;
  const pageSize = 7;
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const current = Math.min(page, pages);
  const slice = filtered.slice((current - 1) * pageSize, current * pageSize);

  return (
    <section className="va-hist">
      <div className="va-hist__filters">
        <div>
          <label htmlFor="hist-day">날짜</label>
          <input id="hist-day" className="va-hist__input" type="date" value={day} onChange={(event) => { setDay(event.target.value); setPage(1); }} />
        </div>
        <div>
          <label htmlFor="hist-entrance">입구</label>
          <select id="hist-entrance" className="va-hist__input" value={entrance} onChange={(event) => { setEntrance(event.target.value); setPage(1); }}>
            {entrances.map((name) => <option key={name}>{name}</option>)}
          </select>
        </div>
      </div>
      <div className="va-hist__stats">
        <div className="va-hist__stat is-blue"><span>전체</span><strong>{filtered.length}</strong></div>
        <div className="va-hist__stat is-green"><span>정상</span><strong>{pass}</strong></div>
        <div className="va-hist__stat is-red"><span>거부</span><strong>{deny}</strong></div>
        <div className="va-hist__stat is-orange"><span>오류</span><strong>{error}</strong></div>
      </div>
      <div className="va-hist__table">
        <div className="va-hist__grid va-hist__grid-head"><span>시간</span><span>표시 코드</span><span>결과</span><span>입구</span></div>
        {slice.map((item) => (
          <div className="va-hist__grid" key={item.id}>
            <span>{seoulClock(item.createdAt)}</span>
            <span>{item.codeMask}</span>
            <span className={histClass(item.decision)}>{COPY[item.decision]?.short ?? item.decision}</span>
            <span>{item.entranceName ?? ""}</span>
          </div>
        ))}
      </div>
      {!loaded && <p className="va-note">이력을 불러오는 중입니다.</p>}
      {loaded && filtered.length === 0 && <p className="va-note">이 조건의 이력이 없습니다.</p>}
      <div className="va-hist__pager">
        <button className="va-hist__page" type="button" onClick={() => setPage(Math.max(1, current - 1))} aria-label="이전">&lt;</button>
        {Array.from({ length: pages }, (_, index) => index + 1).slice(0, 5).map((number) => (
          <button key={number} className={number === current ? "va-hist__page is-on" : "va-hist__page"} type="button" onClick={() => setPage(number)}>{number}</button>
        ))}
        <button className="va-hist__page" type="button" onClick={() => setPage(Math.min(pages, current + 1))} aria-label="다음">&gt;</button>
      </div>
    </section>
  );
}

function SampleScreen() {
  const [tickets, setTickets] = useState<Array<{ code: string; label: string; vendor: string }>>([]);
  const [images, setImages] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.demoTickets().then(async (body) => {
      setTickets(body.tickets);
      const next: Record<string, string> = {};
      for (const ticket of body.tickets) {
        if (ticket.vendor === "b") {
          const canvas = document.createElement("canvas");
          JsBarcode(canvas, ticket.code, { format: "CODE128", displayValue: true });
          next[ticket.code] = canvas.toDataURL();
        } else {
          next[ticket.code] = await QRCode.toDataURL(ticket.code, { margin: 1, width: 220 });
        }
      }
      setImages(next);
    }).catch(() => setTickets([])).finally(() => setLoaded(true));
  }, []);
  if (!loaded) return <p className="va-page va-note">샘플 코드를 불러오는 중입니다.</p>;
  if (!tickets.length) return <p className="va-page va-note">이 화면의 샘플 코드는 시뮬레이션 고객사에서만 제공됩니다.</p>;
  return (
    <section className="va-page">
      {tickets.map((ticket) => (
        <article className="va-panel" key={ticket.code}>
          <strong>{ticket.label}</strong>
          <p>{ticket.code}</p>
          {images[ticket.code] && <img alt={ticket.label} src={images[ticket.code]} />}
        </article>
      ))}
    </section>
  );
}

function OpsScreen() {
  const [commands, setCommands] = useState<Array<{ id: string; status: string; kind: string; result_detail: { message?: string } }>>([]);
  const [gates, setGates] = useState<Array<{ entrance_id: string; entrance_name: string; mode: string; adapter_code: string }>>([]);
  const [stats, setStats] = useState<Array<{ decision: string; count: number; admitted: number }>>([]);
  const [message, setMessage] = useState("");
  const load = () => {
    api.commands().then((body) => setCommands(body.items)).catch((error) => setMessage(error.message));
    api.gates().then((body) => setGates(body.items)).catch(() => undefined);
    api.stats().then((body) => setStats(body.items)).catch(() => undefined);
  };
  useEffect(load, []);
  return (
    <section className="va-page">
      <div className="va-panel">
        <h2>게이트 제어</h2>
        <p>기본값은 시뮬레이션입니다. 이 화면의 명령은 실제 게이트를 열지 않습니다.</p>
        {gates.map((gate) => (
          <div key={gate.entrance_id} className="va-inline">
            <span>{gate.entrance_name} · {gate.mode} · {gate.adapter_code}</span>
            <button className="va-btn" type="button" onClick={() => api.openGate(gate.entrance_id).then(() => { setMessage("수동 개방 요청을 기록했습니다."); load(); }).catch((error) => setMessage(error.message))}>수동 개방</button>
          </div>
        ))}
        {message && <p>{message}</p>}
        {commands.map((command) => (
          <div className="va-info__row" key={command.id}><dt>{command.kind}</dt><dd>{command.status} {command.result_detail?.message ?? ""}</dd></div>
        ))}
      </div>
      <div className="va-panel">
        <h2>최근 24시간</h2>
        {stats.map((item) => <p key={item.decision}>{COPY[item.decision]?.short ?? item.decision}: {item.count}건, 입장 {item.admitted}명</p>)}
      </div>
    </section>
  );
}

function AdminScreen({ session }: { session: Session }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("scanner");
  const [staff, setStaff] = useState<Array<{ email: string; display_name: string; role: string }>>([]);
  const [message, setMessage] = useState("");
  const [tenants, setTenants] = useState<Array<{ id: string; code: string; name: string; license_end: string; max_devices: number; status: string }>>([]);
  useEffect(() => {
    if (session.role !== "platform_admin") {
      api.staff().then((body) => setStaff(body.items)).catch((error) => setMessage(error.message));
    } else {
      api.tenants().then((body) => setTenants(body.items)).catch((error) => setMessage(error.message));
    }
  }, [session.role]);
  if (session.role === "platform_admin") {
    return (
      <section className="va-page">
        <div className="va-panel">
          <h2>고객사 사용 기간·단말</h2>
          <p>기능 옵션과 단말 수는 서버 설정으로 관리합니다. 비밀번호는 이 화면에 다시 표시하지 않습니다.</p>
          {tenants.map((tenant) => (
            <form key={tenant.id} onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              api.updateTenant(tenant.id, {
                licenseEnd: String(form.get("licenseEnd")),
                maxDevices: Number(form.get("maxDevices")),
                status: String(form.get("status")),
              }).then(() => setMessage(`${tenant.name} 저장`)).catch((error) => setMessage(error.message));
            }}>
              <strong>{tenant.name} ({tenant.code})</strong>
              <label>사용 종료일</label>
              <input className="va-field" name="licenseEnd" defaultValue={String(tenant.license_end).slice(0, 10)} />
              <label>최대 스캐너 수</label>
              <input className="va-field" name="maxDevices" type="number" defaultValue={tenant.max_devices} />
              <label>상태</label>
              <select className="va-field" name="status" defaultValue={tenant.status}>
                <option value="active">사용</option>
                <option value="suspended">중지</option>
              </select>
              <button className="va-btn" type="submit">저장</button>
            </form>
          ))}
          {message && <p>{message}</p>}
        </div>
      </section>
    );
  }
  return (
    <section className="va-page">
      <div className="va-panel">
        <h2>직원</h2>
        <form onSubmit={(event) => {
          event.preventDefault();
          api.createStaff({ email, displayName: email, password, role }).then(() => {
            setMessage("직원을 저장했습니다. 초기 비밀번호는 이 화면을 벗어난 뒤 다시 표시되지 않습니다.");
            setPassword("");
            return api.staff();
          }).then((body) => setStaff(body.items)).catch((error) => setMessage(error.message));
        }}>
          <label>이메일</label>
          <input className="va-field" value={email} onChange={(event) => setEmail(event.target.value)} />
          <label>초기 비밀번호</label>
          <input className="va-field" type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
          <label>역할</label>
          <select className="va-field" value={role} onChange={(event) => setRole(event.target.value)}>
            <option value="scanner">검표원</option>
            <option value="supervisor">감독</option>
            <option value="tenant_admin">고객사 관리자</option>
          </select>
          <button className="va-btn" type="submit">저장</button>
        </form>
        {message && <p>{message}</p>}
        {staff.map((item) => (
          <div className="va-info__row" key={item.email}><dt>{item.display_name}</dt><dd>{item.email} · {roleLabel(item.role)}</dd></div>
        ))}
      </div>
    </section>
  );
}

function roleLabel(role: string) {
  return { platform_admin: "플랫폼 관리자", tenant_admin: "고객사 관리자", supervisor: "감독", scanner: "검표원" }[role] ?? role;
}
